import React, { useState, useEffect } from 'react';
import { useAuth } from '../../Common/Configurations/AuthContext';
import apiService from '../../Common/Configurations/APIService';
import config from '../../Common/Configurations/APIConfig';
import './MyProfile.css';

const initialValues = {
    oldPassword: '',
    newPassword: '',
    confirmNewPassword: '',
};

const ChangePasswordForm = () => {
    const { user } = useAuth();
    const [values, setValues] = useState(initialValues);
    const [errors, setErrors] = useState({});
    const [show, setShow] = useState({ old: false, new: false, confirm: false });
    const [isLoading, setIsLoading] = useState(false);
    const [apiError, setApiError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        if (!successMessage) return;
        const t = setTimeout(() => setSuccessMessage(''), 5000);
        return () => clearTimeout(t);
    }, [successMessage]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setValues({ ...values, [name]: value });
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: undefined }));
        if (apiError) setApiError('');
        if (successMessage) setSuccessMessage('');
    };

    const validate = () => {
        const tempErrors = {};
        if (!values.oldPassword.trim()) tempErrors.oldPassword = 'Current password is required.';
        if (!values.newPassword.trim()) {
            tempErrors.newPassword = 'New password is required.';
        } else if (values.newPassword.length < 6) {
            tempErrors.newPassword = 'Must be at least 6 characters.';
        }
        if (values.newPassword !== values.confirmNewPassword) {
            tempErrors.confirmNewPassword = 'Passwords do not match.';
        }
        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setApiError('');
        setSuccessMessage('');
        if (!validate()) return;
        setIsLoading(true);
        try {
            await apiService.post(config.CHANGEPASSWORD, {
                userId: user.id,
                oldPassword: values.oldPassword,
                newPassword: values.newPassword,
            });
            setSuccessMessage('Password changed successfully!');
            setValues(initialValues);
            setErrors({});
        } catch {
            setApiError('Failed to update password. Please check your current password.');
        } finally {
            setIsLoading(false);
        }
    };

    const PasswordField = ({ label, name, showKey }) => (
        <div className="mb-3">
            <label className="profile-field-label">{label}</label>
            <div className="password-input-wrap">
                <input
                    type={show[showKey] ? 'text' : 'password'}
                    name={name}
                    value={values[name]}
                    onChange={handleInputChange}
                    className={`profile-form-control${errors[name] ? ' is-invalid' : ''}`}
                    placeholder="••••••••"
                />
                <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShow(s => ({ ...s, [showKey]: !s[showKey] }))}
                    tabIndex={-1}
                >
                    {show[showKey] ? '🙈' : '👁'}
                </button>
            </div>
            {errors[name] && <div className="invalid-feedback d-block" style={{ fontSize: '0.78rem' }}>{errors[name]}</div>}
        </div>
    );

    return (
        <div className="password-card">
            <div className="password-card-header">
                <div className="password-icon-wrap">🔒</div>
                <h5 className="password-card-title">Change Password</h5>
            </div>
            <div className="password-card-body">
                {apiError && <div className="alert alert-danger py-2 mb-3" style={{ fontSize: '0.85rem' }}>{apiError}</div>}
                {successMessage && <div className="alert alert-success py-2 mb-3" style={{ fontSize: '0.85rem' }}>{successMessage}</div>}
                <form onSubmit={handleSubmit} noValidate>
                    <PasswordField label="Current Password" name="oldPassword" showKey="old" />
                    <PasswordField label="New Password" name="newPassword" showKey="new" />
                    <PasswordField label="Confirm New Password" name="confirmNewPassword" showKey="confirm" />
                    <button type="submit" className="btn-update-password" disabled={isLoading}>
                        {isLoading ? 'Updating…' : 'Update Password'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ChangePasswordForm;
